 
import { Stack, useTheme } from '@mui/joy';
import { scaleLinear } from 'd3-scale';
import { arc } from 'd3-shape';
import RadialLegend from './RadialLegend';
import CircleType from 'circletype';
import { useEffect, useRef } from 'react';
import UiTooltip from '../Controls/UiTooltip';
import ChartTooltip from './ChartTooltip';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { SM_PERCENT_FORMATTER } from '../../lib/format';
import { animated, useSpring } from '@react-spring/web';

const SimpleRadialChart = ({
	office,
	topic,
	small,
	chartData
}: {
	office: string;
	topic: string;
	small?: boolean;
	chartData: {
		group: string; //'MenDEM' | 'WomenDEM' | 'WomenGOP' | 'MenGOP' | 'MenIND' | 'WomenIND';
		partyLabel: string; //'DEM' | 'GOP' | 'IND' ;
		chamber: string; //US Senate | US House;
		label: string; //'Men' | 'Women' | 'Independent';
		value: number;
		numCand: number; //N
	}[];
}) => {
	const prevData = useRef<{ [x: string]: string | number }[]>([]);
	const theme = useTheme();
	const COLORS: { [key: string]: string } = {
		MenDEM: theme.colorSchemes.light.palette.categoryPartyDemocratMen.solidBg,
		MenGOP: theme.colorSchemes.light.palette.categoryPartyRepublicanMen.solidBg,
		WomenDEM:
			theme.colorSchemes.light.palette.categoryPartyDemocratWomen.solidBg,
		WomenGOP:
			theme.colorSchemes.light.palette.categoryPartyRepublicanWomen.solidBg,
		MenIND:
			theme.colorSchemes.light.palette.categoryPartyIndependentMen.solidBg,
		WomenIND:
			theme.colorSchemes.light.palette.categoryPartyIndependentWomen.solidBg
	};
	// const GOPRed: string =
	// 	theme.colorSchemes.light.palette.categoryPartyRepublicanWomen.solidBg;
	// const DEMBlue: string =
	// 	theme.colorSchemes.light.palette.categoryPartyDemocratWomen.solidBg;
	// const INDPurple: string =
	// 	theme.colorSchemes.light.palette.categoryPartyIndependentWomen.solidBg;
	//const { election, congressTab, selectedState } = useRoute();
	const configuredData = {
		0: chartData[0],
		1: chartData[1],
		2: undefined,
		3: chartData[2],
		4: chartData[3]
	};

	const maxRadius = small ? 80 : 120;
	const maxValue = 100;
	 
	const scale = scaleLinear().domain([0, 100]).range([50, 170]) as (
		arg0: number
	) => number;

	const donutHole = (50 / maxValue) * maxRadius;
	const polarData: { [x: string]: string | number }[] = Object.values(
		configuredData
	).map((d, i) => {
		return d === undefined
			? //this puts blank spacer in when there is no value for the group
			  {
					value: scale(0),
					percent: 0,
					group: 'blank',
					startAngle: (i * Math.PI * 2) / 10, //because 5 sections in a half circle. If wanted whole circle, would be 10 sections
					endAngle: (i * Math.PI * 2) / 10 + (Math.PI * 2) / 10,
					innerRadius: donutHole,
					outerRadius: donutHole + 0.001, //need to have some value that is larger than innerRadius in order for animation to work
					color: 'transparent',
					opacity: 1,
					numCand: 0
			  }
			: {
					value:
						//make segments with zero value be filled with gray
						SM_PERCENT_FORMATTER(d.value) === '0.00%'
							? scale(100)
							: scale(d.value * 100), //makes a zero value into 100 so we can create gray slice
					percent: d.value,
					group: d.group,
					startAngle: (i * Math.PI * 2) / 10, //because 5 sections in a half circle. If wanted whole circle, would be 10 sections
					endAngle: (i * Math.PI * 2) / 10 + (Math.PI * 2) / 10,
					innerRadius: donutHole,
					outerRadius:
						SM_PERCENT_FORMATTER(d.value) === '0.00%'
							? (scale(100) / maxValue) * maxRadius + 0.001
							: (scale(d.value * 100) / maxValue) * maxRadius + 0.001, //adding .001 to prevent val from being zero which causes animation problems
					color:
						SM_PERCENT_FORMATTER(d.value) === '0.00%'
							? '#DADADA'
							: COLORS[d.group],
					opacity: SM_PERCENT_FORMATTER(d.value) === '0.00%' ? 0.5 : 1,
					numCand: d.numCand
			  };
	});

	 
	const polarArc = arc() as (arg0: unknown) => string;

	//dashed lines
	const lineData = [
		{ group: 1, value: 0 },
		{ group: 2, value: 50 },
		{ group: 3, value: 100 }
	];
	const polarLines = lineData.map((d) => {
		return {
			value: scale(d.value),
			startAngle: 0,
			endAngle: (Math.PI * 2) / 2,
			outerRadius: (scale(d.value) / maxValue) * maxRadius,
			innerRadius: (scale(d.value) / maxValue) * maxRadius, //added to get rid of error
			group: d.group
		};
	});

	//arced colored line
	const labelLineData: { group: string; value: number; color: string }[] = [
		{ group: 'MDEM', value: 107, color: COLORS.WomenDEM },
		{ group: 'WDEM', value: 107, color: COLORS.WomenDEM },
		{ group: 'IND', value: 107, color: COLORS.WomenIND },
		{ group: 'WGOP', value: 107, color: COLORS.WomenGOP },
		{ group: 'MGOP', value: 107, color: COLORS.WomenGOP }
	];
	const polarLabelLine = labelLineData.map((d, i) => {
		return {
			value: scale(d.value),
			startAngle:
				i === 2 ? (i * Math.PI * 2) / 10 + 0.02 : (i * Math.PI * 2) / 10, //because 5 sections in a half circle. If wanted whole circle, would be 10 sections
			endAngle:
				i === 2
					? (i * Math.PI * 2) / 10 + (Math.PI * 2) / 10 - 0.02
					: (i * Math.PI * 2) / 10 + (Math.PI * 2) / 10,
			outerRadius: (scale(d.value) / maxValue) * maxRadius,
			innerRadius: (scale(d.value) / maxValue) * maxRadius, //added to get rid of error
			 
			// @ts-ignore
			color:
				i === 2 && configuredData[i] === undefined ? 'transparent' : d.color,
			group: d.group
		};
	});
	const title = useRef<HTMLDivElement>(null);

	useEffect(() => {
		 
		// @ts-ignore
		if (small)
			 
			new CircleType(title.current).radius(200);
		 
		else new CircleType(title.current).radius(300);
	}, [small]);

	const hasInd = configuredData[2];

	const width = small ? 230 : 250; //250
	const height = small ? 300 : 430; //430

	const matches = useMediaQuery(theme.breakpoints.up('sm'));

	// const hasEdgeCases =
	// 	STATES_WITH_EDGE_CASES.includes(selectedState as string) &&
	// 	page === 'state' &&
	// 	election === 'state';

	useEffect(() => {
		prevData.current = polarData;
	});

	const path = small
		? 'M 25, 50 a 25,25 0 1,1 50,0 25,25 0 1,1 -50,0'
		: 'M 10, 50 a 40,40 0 1,1 80,0 40,40 0 1,1 -80,0';
	return (
		<Stack
			direction='column'
			sx={{
				flex: 1,
				alignItems: 'center',
				px: '1rem',
				position: 'relative',
				justifyContent: 'flex-end',
				padding: 0
			}}
		>
			<div
				style={{
					color: 'black',
					fontSize: small ? '1.5rem' : '2rem',
					fontFamily: 'Source Sans Pro, sans-serif',
					fontWeight: 600,
					marginTop: '1.5rem',
					letterSpacing: small ? '-2px' : 0,
					height: small ? '3rem' : '4rem'
				}}
				ref={title}
			>
				{office.toUpperCase()}
			</div>

			<>
				<svg
					width={height}
					height={width}
					aria-label='a radial chart showing the percentage of money for each party and gender.'

					//viewBox={`${-width / 2}, ${-height / 2}, ${width}, ${height}`}
					//transform={`rotate(-90) translate(100, 0)
					//style={{ transform: 'rotate(-90deg) translate(100px, 0)' }}
				>
					<g
						transform={
							small
								? `rotate(-90) translate(-170, 0)`
								: `rotate(-90) translate(-250, 0)`
						}
					>
						{polarLines.map((p, i) => (
							<g
								key={`${i}${p.group}`}
								transform={`translate(0,${small ? 150 : 215})`}
							>
								<path
									d={polarArc(p)}
									fill={'transparent'}
									stroke={'gray'}
									strokeDasharray={'4,4'}
									opacity={0.5}
								></path>
							</g>
						))}
						<g transform='rotate(90)'>
							<rect
								fill={theme.palette.chartElementsBackground.bg}
								width='30px'
								height='20px'
								x={small ? '135' : '205'}
								y={small ? '-147' : '-215'}
							></rect>

							<text
								x={small ? '135' : '205'}
								y={small ? '-132' : '-200'}
								fill='#333'
								fontSize='12px'
							>
								100%
							</text>
						</g>
						<g transform='rotate(90)'>
							<rect
								fill={theme.palette.chartElementsBackground.bg}
								width='25px'
								height='20px'
								x={small ? '138' : '205'}
								y={small ? '-97' : '-142'}
							></rect>

							<text
								x={small ? '140' : '208'}
								y={small ? '-83' : '-127'}
								fill='#333'
								fontSize='12px'
							>
								50%
							</text>
						</g>
						{polarLabelLine.map((p, i) => (
							<g
								key={`${i}${p.group}${i}`}
								transform={`translate(0,${small ? 150 : 215})`}
							>
								<path
									d={polarArc(p)}
									fill={'transparent'}
									stroke={p.color}
									strokeWidth={2}
								></path>
							</g>
						))}
						{/* this animates but hover does not produce tooltip */}
						{polarData.map((p, i) => (
							<g
								key={`${p.group}${i}${i}`}
								transform={`translate(0,${small ? 150 : 215})`}
							>
								<Arc
									key={`${i}${p.group}`}
									fromAngle={
										prevData.current.length === 0 ? p : prevData.current[i]
									}
									toAngle={p}
									arcGenerator={polarArc}
								/>
							</g>
						))}
						{/* this is transparent copy that sits on top that provides tooltip. */}
						{polarData.map((p, i) => {
							return (
								<g
									key={`${p.group}${i}${i}`}
									transform={`translate(0,${small ? 150 : 215})`}
								>
									<UiTooltip
										title={
											<ChartTooltip titleText={office} d={p} topic={topic} />
										}
										variant='plain'
										sx={{ borderRadius: '0px' }}
										//arrow
										placement={!matches ? 'top' : 'left'}
									>
										<g>
											<path
												d={polarArc({
													...p,
													outerRadius:
														p.group === 'blank'
															? donutHole
															: (scale(100) / maxValue) * maxRadius + 0.001 // this makes all transparent overlay take up the full slice
													//so that the tooltip is triggered when hovering over the entire slice
												})}
												fill={'transparent'}
												//fill={p.color as string}
												//opacity={p.color === '#DADADA' ? 0.5 : 1}
											></path>
										</g>
									</UiTooltip>
								</g>
							);
						})}
						<g
							transform={
								small ? 'rotate(140) scale(1.1)' : 'rotate(142) scale(1.1)'
							}
						>
							<g
								transform={small ? 'translate(37,-155)' : 'translate(70,-203)'}
							>
								{/* //45,-150 */}
								<path
									//stroke={'red'}
									id='circlePath'
									fill='none'
									d={path}
								/>
								<text fill='black' fontSize={small ? '9px' : '14px'}>
									<textPath href='#circlePath'>WOMEN</textPath>
								</text>
							</g>
						</g>
						<g transform={'rotate(91) scale(1.1)'}>
							<g transform={small ? 'translate(85,-53)' : 'translate(145,-53)'}>
								<text fill='black' fontSize={small ? '7px' : '10px'}>
									<textPath href='#circlePath'>MEN</textPath>
								</text>
							</g>
						</g>
						<g transform={'rotate(-120) scale(1.1)'}>
							<g
								transform={
									small ? 'translate(-170,-117)' : 'translate(-220,-147)'
								}
							>
								<text fill='black' fontSize={small ? '7px' : '10px'}>
									<textPath href='#circlePath'>MEN</textPath>
								</text>
							</g>
						</g>

						<g transform='rotate(90)'>
							{/* <rect
							fill={
								hasInd
									? theme.colorSchemes.light.palette
											.categoryPartyIndependentWomen.solidBg
									: theme.palette.chartElementsBackground.bg
							}
							width='25px'
							height='20px'
							x='205'
							y={small ? '-60' : '-84'}
						></rect> */}

							<text
								x={small ? '143' : '212'}
								y={small ? '-45' : '-64'}
								fill={hasInd ? 'white' : '#333'}
								fontSize='12px'
							>
								0%
							</text>
						</g>

						<g transform='rotate(40)'>
							<text
								x={small ? '70' : '112'}
								y={small ? '-41' : '-65'}
								fill={
									theme.colorSchemes.light.palette.categoryPartyDemocratWomen
										.solidBg
								}
								fontSize={small ? '16px' : '22px'}
								fontWeight={600}
								letterSpacing={'4px'}
							>
								DEM
							</text>
						</g>

						<g transform='rotate(140)'>
							<text
								x={small ? '78' : '110'}
								y={small ? '-271' : '-395'}
								fill={
									theme.colorSchemes.light.palette.categoryPartyRepublicanWomen
										.solidBg
								}
								fontSize={small ? '16px' : '22px'}
								fontWeight={600}
								letterSpacing={'4px'}
							>
								GOP
							</text>
						</g>

						{hasInd ? (
							<g transform='rotate(90)'>
								<text
									x='198'
									y='-230'
									fill={
										theme.colorSchemes.light.palette.categoryPartyIndependent
											.solidBg
									}
									fontSize='22px'
									fontWeight={600}
									letterSpacing={'4px'}
								>
									IND
								</text>
							</g>
						) : null}
					</g>
				</svg>

				<RadialLegend
					showInd={configuredData[2] ? true : false}
					small={small}
					index={1}
					count={1}
				/>
			</>

			{/* <Box sx={{ marginTop: '3rem', width: '50%' }}>
				{chartData.map((item) => (
					<Stack direction='row' key={item.group}>
						<div>{item.group}</div>
						<div>{item.value}</div>
					</Stack>
				))}
			</Box> */}
		</Stack>
	);
};

export default SimpleRadialChart;

interface ArcProps {
	fromAngle: { [x: string]: string | number };
	toAngle: { [x: string]: string | number };
	 
	arcGenerator: any;
	// mouseLeave: React.MouseEventHandler<SVGGElement> | undefined;
	// mouseEnter: React.MouseEventHandler<SVGGElement> | undefined;
	// handleClick: React.MouseEventHandler<SVGGElement> | undefined;
}
const Arc = ({ fromAngle, toAngle, arcGenerator }: ArcProps) => {
	const arcSpring = useSpring({
		from: {
			 
			d: arcGenerator(fromAngle) as string,
			color: fromAngle.color as string,
			opacity: fromAngle.opacity as number
		},
		to: {
			 
			d: arcGenerator(toAngle) as string,
			color: toAngle.color as string,
			opacity: toAngle.opacity as number
		}
	});
	return (
		<g>
			<animated.path
				d={arcSpring.d}
				fill={arcSpring.color}
				opacity={arcSpring.opacity}
			></animated.path>
		</g>
	);
};
